// Market-extension section — remembr isn't just for dementia. It's a lifetime
// memory builder. Lavender for the loved ones we're letting go of; baby blue and
// baby pink for the brand-new ones we're just meeting. Same forget-me-not. Same
// "Good morning, [name]". Same family voices.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";
import { TeddyIcon } from "../TeddyIcon";
import { Heart } from "@phosphor-icons/react";

const FADE = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export function EveryLife() {
    const { t } = useTranslation();

    const EDITIONS = [
        {
            key: "newborn-boy",
            accentBg: "bg-[#EAF2F9]",
            accentText: "text-[#3F6C99]",
            eyebrow: t("every_life.editions.boy_eyebrow"),
            title: t("every_life.editions.boy_title"),
            body: t("every_life.editions.boy_body"),
            img: "/product-mockup-kids-blue.png",
            chip: t("every_life.editions.boy_chip"),
            chipIcon: TeddyIcon,
            chipColor: "#3F6C99",
        },
        {
            key: "dementia",
            accentBg: "bg-[#F4F6FB]",
            accentText: "text-[#4A5A95]",
            eyebrow: t("every_life.editions.lavender_eyebrow"),
            title: t("every_life.editions.lavender_title"),
            body: t("every_life.editions.lavender_body"),
            img: "/product-mockup-1.png",
            chip: t("every_life.editions.lavender_chip"),
            chipIcon: Heart,
            featured: true,
        },
        {
            key: "newborn-girl",
            accentBg: "bg-[#FBF1F2]",
            accentText: "text-[#A85D62]",
            eyebrow: t("every_life.editions.girl_eyebrow"),
            title: t("every_life.editions.girl_title"),
            body: t("every_life.editions.girl_body"),
            img: "/product-mockup-kids-pink.png",
            chip: t("every_life.editions.girl_chip"),
            chipIcon: TeddyIcon,
            chipColor: "#A85D62",
        },
    ];

    return (
        <section
            id="every-life"
            className="relative py-32 px-6 bg-gradient-to-b from-white via-[#FBF8F2] to-white overflow-hidden"
            data-testid="every-life-section"
        >
            <FmnWatermark position="left" size={780} opacity={0.05} rotate={-8} />
            <div className="max-w-7xl mx-auto">
                {/* Intro */}
                <motion.div {...FADE} className="text-center mb-20 max-w-3xl mx-auto">
                    <p className="font-sans text-xs uppercase tracking-[0.32em] text-[#6B7DB8] font-semibold mb-6">
                        {t("every_life.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-tight">
                        {t("every_life.headline_part1")} <em className="italic text-[#A8C8E0]">{t("every_life.headline_first")}</em> {t("every_life.headline_part2")}<br />
                        {t("every_life.headline_part3")} <em className="italic text-[#6B7DB8]">{t("every_life.headline_last")}</em>
                    </h2>
                    <p className="mt-7 text-[#5C5A59] leading-relaxed max-w-2xl mx-auto text-base sm:text-lg">
                        {t("every_life.intro_part1")} <em className="italic">{t("every_life.intro_question")}</em> {t("every_life.intro_part2")} <span className="text-[#4A5A95]">{t("every_life.intro_emphasis")}</span>
                    </p>
                </motion.div>

                {/* Three editions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
                    {EDITIONS.map((e, i) => {
                        const ChipIcon = e.chipIcon;
                        return (
                            <motion.div
                                key={e.key}
                                {...FADE}
                                transition={{ ...FADE.transition, delay: 0.1 + i * 0.12 }}
                                className={`group relative rounded-3xl overflow-hidden border ${e.featured ? "border-[#6B7DB8]/40 shadow-rem-card" : "border-[#E5E1D8] shadow-rem-soft"} ${e.accentBg} hover:-translate-y-1 transition-all duration-500`}
                                data-testid={`edition-${e.key}`}
                            >
                                {e.featured && (
                                    <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-[#F4D068] text-[#2D2B2A] text-[10px] font-semibold uppercase tracking-wider">
                                        {t("every_life.where_it_began")}
                                    </div>
                                )}
                                <div className="aspect-square overflow-hidden bg-white">
                                    <img
                                        src={e.img}
                                        alt={e.title}
                                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-7">
                                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/70 text-[10px] font-semibold uppercase tracking-wider ${e.accentText}`}>
                                        <ChipIcon size={14} weight="bold" color={e.chipColor || "currentColor"} />
                                        {e.chip}
                                    </div>
                                    <p className={`mt-4 text-[10px] uppercase tracking-[0.28em] font-semibold ${e.accentText}`}>{e.eyebrow}</p>
                                    <h3 className="mt-2 font-serif text-2xl text-[#2D2B2A]">{e.title}</h3>
                                    <p className="mt-3 text-sm text-[#5C5A59] leading-relaxed">{e.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Packaging */}
                <motion.div {...FADE} className="relative">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center bg-white rounded-[2.5rem] p-8 sm:p-12 border border-[#E5E1D8]/70 shadow-rem-card">
                        <div className="lg:col-span-3 rounded-[2rem] overflow-hidden">
                            <img
                                src="/product-mockup-packaging.png"
                                alt="The three remembr gift box editions — lavender, baby blue, and soft pink — each carrying the forget-me-not wordmark"
                                className="w-full h-auto object-cover"
                                loading="lazy"
                                data-testid="packaging-mockup"
                            />
                        </div>
                        <div className="lg:col-span-2">
                            <h3 className="font-serif text-3xl sm:text-4xl text-[#2D2B2A] leading-tight">
                                {t("every_life.packaging.headline_part1")}<br/>
                                <em className="italic text-[#6B7DB8]">{t("every_life.packaging.headline_part2")}</em>
                            </h3>
                            <div className="mt-7 space-y-4 text-[#5C5A59] text-sm leading-relaxed">
                                <p>{t("every_life.packaging.body_1")}</p>
                                <p>{t("every_life.packaging.body_2")}</p>
                                <p className="italic text-[#4A5A95]">{t("every_life.packaging.body_3_quote")}</p>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <a
                                    href="#pricing"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-semibold transition-all hover:-translate-y-0.5"
                                    data-testid="every-life-cta-pricing"
                                >
                                    {t("every_life.packaging.cta_reserve")}
                                </a>
                                <a
                                    href="/demo/john"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-transparent border border-[#E5E1D8] text-[#5C5A59] hover:text-[#6B7DB8] hover:border-[#6B7DB8] text-sm font-medium transition-all"
                                    data-testid="every-life-cta-demo"
                                >
                                    {t("every_life.packaging.cta_demo")}
                                </a>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Forget-me-not signature */}
                <motion.div {...FADE} className="flex flex-col items-center mt-20">
                    <ForgetMeNotIcon size={36} />
                    <p className="mt-4 text-xs uppercase tracking-[0.32em] text-[#8A8786] font-medium">
                        {t("every_life.signature")}
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
